import { EImportSectionsTarget } from '@constants';
import { Collapse, Flex, Input } from 'antd';
import { isEmpty } from 'lodash';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { TitleWrapper, Wrapper } from './Sections.styles';

import { Activities } from '@/components/Activities';
import { ActivityPreview } from '@/components/ActivityPreview';
import { ContentSearchEmptyPage } from '@/components/ContentSearchEmptyPage';
import { Icon } from '@/components/Icon';
import {
  AddSection,
  EmptySections,
  ImportSection,
  SectionActions
} from '@/components/SectionManagement';
import { SectionName } from '@/components/SectionName';
import { SortableProvider } from '@/components/SortableProvider';
import { Box } from '@/components/Utils';
import { useSearchSections } from '@/hooks';
import { SectionDescription } from '@/pages/Common/Course/components/Sections/SectionDescription';
import { useLocalCourse } from '@/store';
import { ECourseContentStep } from '@/types';

export const Sections: React.FC = () => {
  const [openSectionIds, setOpenSectionIds] = useState<string[]>([]);
  const { t } = useTranslation('ManagerBaseContentDetailTranslations');
  const { course, reorderCourseSections } = useLocalCourse();
  const { colors } = useTheme();
  const target = EImportSectionsTarget.SECTIONS;
  const [searchParams] = useSearchParams();
  const sectionId = searchParams.get('sectionId');
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (!sectionId) return;

    setOpenSectionIds(prev => (prev.includes(sectionId) ? prev : [...prev, sectionId]));

    const timeoutId = setTimeout(() => {
      const sectionElement = sectionRefs.current[sectionId];
      if (sectionElement) {
        sectionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [sectionId]);
  const { searchValue, setSearchValue, filteredSections } = useSearchSections(course?.sections);

  if (!course) return null;

  const { sections = [] } = course;
  const isSectionsEmpty = isEmpty(sections);
  const isSectionsFilteredEmpty = isEmpty(filteredSections) && !isSectionsEmpty;

  const handleReorder = (items: ReactNode[]) => {
    const newSectionsOrder = items.map(
      item => (item as React.ReactElement<{ _id: string }>).props._id
    );

    reorderCourseSections({ courseId: course._id, newSectionsOrder });
  };

  return (
    <Wrapper>
      <TitleWrapper>{t(ECourseContentStep.SECTIONS)}</TitleWrapper>
      <Flex justify="space-between" align="center">
        {!isSectionsEmpty && (
          <Input.Search
            placeholder={t('SEARCH_SECTIONS_OR_ACTIVITIES_BY_NAME')}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            allowClear
            style={{ width: '100%', maxWidth: 400 }}
          />
        )}
        <Flex gap={8}>
          {!isSectionsEmpty && (
            <>
              <AddSection course={course} target={target} />
              <ImportSection target={target} />
            </>
          )}
        </Flex>
      </Flex>
      {isSectionsEmpty && <EmptySections course={course} target={target} />}
      {isSectionsFilteredEmpty && <ContentSearchEmptyPage />}
      <SortableProvider
        zIndex={10}
        droppableId="sections"
        onReorder={handleReorder}
        isDragDisabled={openSectionIds.length > 0 || !!searchValue}>
        {filteredSections.map((section, index) => (
          <Collapse
            key={`${section._id}-${index}`}
            ref={el => {
              sectionRefs.current[section._id] = el;
            }}
            {...section}
            activeKey={openSectionIds.includes(section._id) ? [section._id] : []}
            style={{
              marginBottom: 16,
              borderRadius: 8,
              backgroundColor: section.active ? colors.black1 : colors.black2
            }}
            onChange={() => {
              setOpenSectionIds(prev =>
                prev.includes(section._id)
                  ? prev.filter(id => id !== section._id)
                  : [...prev, section._id]
              );
            }}
            expandIcon={({ isActive }) => (
              <Box gap={10}>
                <Icon i={isActive ? 'ChevronDown' : 'ChevronRight'} size={16} />
              </Box>
            )}
            items={[
              {
                key: section._id,
                label: (
                  <Flex vertical gap={section.description ? 8 : 0}>
                    <SectionName section={section} />
                    <SectionDescription
                      section={section}
                      isSectionSelected={openSectionIds.includes(section._id)}
                    />
                  </Flex>
                ),
                extra: <SectionActions target={target} courseId={course._id} section={section} />,
                children: <Activities section={section} />
              }
            ]}
          />
        ))}
      </SortableProvider>
      <ActivityPreview />
    </Wrapper>
  );
};
